/**
 * InstallAppBanner — banner-center source: the mobile install offer.
 *
 * There is no native app — the "mobile application" is prest0 installed to
 * the home screen as a PWA. Until now nothing on the platform ever offered
 * that (zero install-prompt handling anywhere), so phone users lived in a
 * browser tab: no icon, no full-screen chrome, no push permission story.
 * This source puts ONE calm offer in the global band, in the house voice,
 * on the platforms where it is real:
 *
 *   - Android (Chromium): the browser hands the page a `beforeinstallprompt`
 *     event; installState.ts captures it at module load. The CTA consumes it
 *     and calls prompt() — the native install sheet appears FROM A CLICK,
 *     never on its own. Accepted (or the browser's `appinstalled`) writes a
 *     permanent installed marker and shows a one-line success; dismissed
 *     snoozes the offer 30 days.
 *   - iOS: there is no prompt API. The CTA opens a body-portaled guidance
 *     sheet (share glyph → "add to home screen" → "add"). Closing it — or the
 *     band's X — snoozes 14 days; the installed app itself runs standalone, so
 *     once they follow the steps the offer never returns. Offered in Safari
 *     AND in Chrome / Edge / Firefox on iOS 16.4+, which have been able to add
 *     to the Home Screen since 2023 (installState.canAddToHomeScreen); reading
 *     that as "Safari only" showed every iPhone user who lives in another
 *     browser nothing at all.
 *   - Everything else (desktop, iOS in-app webviews, Android browsers that
 *     never fire the event): nothing. A control that cannot do its job is
 *     never shown.
 *
 * The experience contract (mirrors AppUpdateBanner exactly):
 *   - SHOWN ONLY WHEN READY: while any tab is mid-answer (the cross-tab
 *     lease in streamingFlag.ts) the offer is held back entirely — no
 *     banner, never a disabled control — and appears the moment the answer
 *     lands. Held time accumulates to READY_FAIL_OPEN_MS so a stuck lease
 *     can never bury it. Once shown it stays clickable.
 *   - Never the first page view: a visitor who just arrived is reading, not
 *     installing. The offer waits for a prior visit (a second browser
 *     session) or two minutes of engagement in this one.
 *   - Signed in, on a banner surface (bannerSurfacePolicy): a signed-out
 *     visitor on a marketing page is not offered an app for an account they
 *     do not have.
 *   - Every snooze is a TIMESTAMP in localStorage (never a latching boolean):
 *     the offer returns on its own; nothing needs clearing.
 *
 * Capability facts come from src/utils/installState.ts (pure detection); the
 * decision to offer, and the copy, live here — one component, auditable.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { BannerBusContext, useRegisterBanner, BannerDescriptor } from './GlobalBannerCenter';
import { useBannerSurface } from './bannerSurface';
import { STREAMING_RECHECK_MS, READY_FAIL_OPEN_MS } from './AppUpdateBanner';
// The lease module directly — one reader, the same one the update banner uses.
import { isAnyTabStreaming } from '../GlobalChatSystem/streamingFlag';
import { BODY_PORTAL_Z_INDEX } from '../GlobalChatSystem/styles';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import {
  consumeDeferredInstallPrompt,
  getDeferredInstallPrompt,
  getMobilePlatform,
  isInstalledRelatedApp,
  canAddToHomeScreen,
  isMobileViewport,
  isStandalone,
  subscribeDisplayMode,
  subscribeInstallState,
  subscribeMobileViewport,
  wasAppInstalledThisPage,
  type MobilePlatform,
} from '../../utils/installState';

// ── Storage keys (all timestamps in ms; never a latching boolean) ────────────

/** Offer snoozed until this epoch-ms (X → 14 days; native prompt dismissed → 30 days). */
export const INSTALL_SNOOZE_KEY = 'prest0_install_snoozed_until';
/** Permanent installed marker: epoch-ms of the accept / appinstalled signal. */
export const INSTALL_INSTALLED_KEY = 'prest0_pwa_installed_at';
/** Count of browser SESSIONS seen (incremented once per session — see INSTALL_SESSION_START_KEY). */
export const INSTALL_VISITS_KEY = 'prest0_install_visits';
/** sessionStorage: epoch-ms this browser session began (a reload keeps it; a new tab/session starts one). */
export const INSTALL_SESSION_START_KEY = 'prest0_install_session_start';

export const DISMISS_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
export const PROMPT_DISMISSED_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
/** A first-time visitor becomes eligible after this long in the session (or on a later session). */
export const ENGAGED_AFTER_MS = 120_000;
/** How long the one-line success lingers in the band before it unregisters itself. */
export const SUCCESS_LINGER_MS = 8_000;

// ── Copy (house voice, lowercase; product copy authored ONCE, here) ──────────

// copy: install_banner_chrome
export const INSTALL_COPY = {
  android: 'prest0 works best as an app on your phone — one tap to install it.',
  androidCta: 'install app',
  ios: 'prest0 works best as an app on your phone — a few taps from the share menu, we’ll show you.',
  iosCta: 'add to home screen',
  success: 'you’re set — open prest0 from your home screen next time.',
  /* The phone lines (2026-09-13). This source is the one that ONLY ever shows
     on a phone, so its full copy was already written for a narrow screen —
     these drop the sales clause ("works best as an app") and keep the action,
     which is all a reader who has already decided needs to see. */
  androidShort: 'install prest0 on your phone — one tap.',
  iosShort: 'add prest0 to your home screen.',
  iosShortCta: 'show me',
  successShort: 'you’re set — open prest0 from your home screen.',
  sheetTitle: 'add prest0 to your home screen',
  sheetStepShare: 'tap the share button in your browser’s toolbar',
  sheetStepAdd: 'scroll and tap “add to home screen”',
  sheetStepConfirm: 'tap “add” in the top corner — that’s it',
  sheetClose: 'got it',
} as const;

// ── Storage helpers (every access wrapped; a locked-down browser reads as "no value") ──

function readMs(storage: 'local' | 'session', key: string): number | null {
  try {
    const s = storage === 'local' ? localStorage : sessionStorage;
    const raw = s.getItem(key);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeMs(storage: 'local' | 'session', key: string, value: number): void {
  try {
    (storage === 'local' ? localStorage : sessionStorage).setItem(key, String(value));
  } catch {
    /* ignore */
  }
}

/** True while a snooze stamp lies in the future. A garbage/absent stamp is "not snoozed". */
export function isInstallSnoozed(nowMs: number = Date.now()): boolean {
  const until = readMs('local', INSTALL_SNOOZE_KEY);
  return until !== null && until > nowMs;
}

/** True once an accept / appinstalled has ever been recorded on this browser. */
export function hasInstalledMarker(): boolean {
  return readMs('local', INSTALL_INSTALLED_KEY) !== null;
}

/**
 * Engagement fact: is this NOT the visitor's first page view? A prior browser
 * session counts (visits ≥ 2), or ENGAGED_AFTER_MS spent in this one. The
 * session start is stamped once per sessionStorage lifetime — a reload keeps
 * it, so reloading cannot fake a second visit.
 */
export function readEngagement(nowMs: number = Date.now()): { engaged: boolean; remainingMs: number } {
  let sessionStart = readMs('session', INSTALL_SESSION_START_KEY);
  let newSession = false;
  if (sessionStart === null) {
    sessionStart = nowMs;
    newSession = true;
    writeMs('session', INSTALL_SESSION_START_KEY, nowMs);
  }
  let visits = readMs('local', INSTALL_VISITS_KEY) ?? 0;
  if (newSession) {
    visits += 1;
    writeMs('local', INSTALL_VISITS_KEY, visits);
  }
  const elapsed = Math.max(0, nowMs - sessionStart);
  return {
    engaged: visits >= 2 || elapsed >= ENGAGED_AFTER_MS,
    remainingMs: Math.max(0, ENGAGED_AFTER_MS - elapsed),
  };
}

// ── Styles ───────────────────────────────────────────────────────────────────

/* Same gold pill as the update banner's "refresh now" — one band, one control family. */
const CtaButton = styled.button`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: #8a6b2a;
  border: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
`;

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: ${BODY_PORTAL_Z_INDEX};
`;

const Sheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${BODY_PORTAL_Z_INDEX};
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  border-radius: 16px 16px 0 0;
  background: var(--brand-canvas);
  color: var(--brand-text);
  border-top: 1px solid rgba(176, 140, 60, 0.35);
  box-shadow: 0 -18px 50px rgba(0, 0, 0, 0.28);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  outline: none;
`;

const Handle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: rgba(176, 140, 60, 0.45);
  margin: 0 auto;
`;

const SheetTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--brand-text);
`;

const Steps = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Step = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
  line-height: 1.35;
`;

const StepGlyph = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--brand-surface);
  border: 1px solid var(--brand-line);
  color: var(--brand-gold-text);
  font-weight: 700;
  font-size: 0.9rem;
  svg { width: 20px; height: 20px; }
`;

const SheetClose = styled.button`
  align-self: stretch;
  margin-top: 4px;
  padding: 12px 16px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  background: var(--brand-black);
`;

/** The iOS share glyph: an arrow rising out of a box. */
const ShareGlyph: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8" />
  </svg>
);

/** The "add to home screen" glyph: a rounded square with a plus. */
const AddGlyph: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="4" y="4" width="16" height="16" rx="4" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
  </svg>
);

// ── iOS guidance sheet ───────────────────────────────────────────────────────

/**
 * Body-portaled (BODY_PORTAL_Z_INDEX, data-prest0n-portal so the chat's
 * outside-click guard leaves it alone), scrim-dismiss, capture-phase Escape,
 * focus taken on open and returned on close — the ThreadListSheet contract.
 */
export const InstallGuideSheet: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    return () => {
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <Scrim data-prest0n-portal="true" data-testid="install-app-sheet-scrim" onClick={onClose} />
      <Sheet
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-app-sheet-title"
        data-prest0n-portal="true"
        data-testid="install-app-sheet"
      >
        <Handle aria-hidden="true" />
        <SheetTitle id="install-app-sheet-title">{INSTALL_COPY.sheetTitle}</SheetTitle>
        <Steps>
          <Step>
            <StepGlyph aria-hidden="true"><ShareGlyph /></StepGlyph>
            <span>{INSTALL_COPY.sheetStepShare}</span>
          </Step>
          <Step>
            <StepGlyph aria-hidden="true"><AddGlyph /></StepGlyph>
            <span>{INSTALL_COPY.sheetStepAdd}</span>
          </Step>
          <Step>
            <StepGlyph aria-hidden="true">add</StepGlyph>
            <span>{INSTALL_COPY.sheetStepConfirm}</span>
          </Step>
        </Steps>
        <SheetClose ref={closeRef} type="button" onClick={onClose} data-testid="install-app-sheet-close">
          {INSTALL_COPY.sheetClose}
        </SheetClose>
      </Sheet>
    </>,
    document.body,
  );
};

// ── The banner source ────────────────────────────────────────────────────────

type Phase = 'offer' | 'success' | 'done';

export const InstallAppBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const onSurface = useBannerSurface();

  // Capability facts (installState.ts) — read once, then kept live by subscription.
  const [mobile, setMobile] = useState<boolean>(() => isMobileViewport());
  const [standalone, setStandalone] = useState<boolean>(() => isStandalone());
  const [platform] = useState<MobilePlatform>(() => getMobilePlatform());
  const [iosInstallable] = useState<boolean>(() => canAddToHomeScreen());
  const [promptAvailable, setPromptAvailable] = useState<boolean>(() => getDeferredInstallPrompt() !== null);
  const [installed, setInstalled] = useState<boolean>(() => hasInstalledMarker() || wasAppInstalledThisPage());
  const [relatedInstalled, setRelatedInstalled] = useState<boolean>(false);
  const [snoozed, setSnoozed] = useState<boolean>(() => isInstallSnoozed());
  const [engaged, setEngaged] = useState<boolean>(false);
  // Readiness (the streaming-lease hold) passed — sticky once true.
  const [shown, setShown] = useState<boolean>(false);
  const [phase, setPhase] = useState<Phase>('offer');
  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  // Ref mirror so the install-event subscription reads the live value without
  // re-subscribing on every render.
  const shownRef = useRef(false);
  useEffect(() => {
    shownRef.current = shown;
  }, [shown]);

  const markInstalled = useCallback(() => {
    writeMs('local', INSTALL_INSTALLED_KEY, Date.now());
    setInstalled(true);
  }, []);

  const snooze = useCallback((ms: number) => {
    writeMs('local', INSTALL_SNOOZE_KEY, Date.now() + ms);
    setSnoozed(true);
  }, []);

  // Live facts.
  useEffect(() => subscribeMobileViewport(setMobile), []);
  useEffect(() => subscribeDisplayMode(setStandalone), []);
  useEffect(
    () =>
      subscribeInstallState((ev) => {
        if (ev === 'prompt-captured') setPromptAvailable(getDeferredInstallPrompt() !== null);
        if (ev === 'app-installed') {
          markInstalled();
          // The success line follows an offer that was actually on screen; an
          // install the browser ran on its own (omnibox, desktop) while we
          // offered nothing simply records the marker and stays quiet.
          setPhase((p) => (p === 'offer' && shownRef.current ? 'success' : p));
        }
        if (ev === 'display-mode') setStandalone(isStandalone());
      }),
    [markInstalled],
  );

  // "Already installed" from the platform, where it can tell us.
  useEffect(() => {
    let cancelled = false;
    void isInstalledRelatedApp().then((yes) => {
      if (!cancelled && yes) setRelatedInstalled(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Not the first page view: prior session, or ENGAGED_AFTER_MS in this one.
  useEffect(() => {
    const { engaged: now, remainingMs } = readEngagement(Date.now());
    if (now) {
      setEngaged(true);
      return undefined;
    }
    const t = setTimeout(() => setEngaged(true), remainingMs);
    return () => clearTimeout(t);
  }, []);

  const platformReady = platform === 'android' ? promptAvailable : platform === 'ios' ? iosInstallable : false;

  const candidate =
    mobile &&
    !standalone &&
    !installed &&
    !relatedInstalled &&
    !snoozed &&
    platformReady &&
    onSurface &&
    engaged;

  // Readiness gate — verbatim AppUpdateBanner: re-check the lease every
  // STREAMING_RECHECK_MS while a candidate offer is not yet shown; show the
  // moment no answer is mid-flight, or once held for READY_FAIL_OPEN_MS in
  // total (accumulated per tick, so a flickering lease cannot restart it).
  useEffect(() => {
    if (!candidate || shown) return undefined;
    let heldMs = 0;
    let lastTick = Date.now();
    const evaluate = () => {
      const now = Date.now();
      const streaming = isAnyTabStreaming(now);
      if (streaming) heldMs += now - lastTick;
      lastTick = now;
      if (!streaming || heldMs >= READY_FAIL_OPEN_MS) {
        setShown(true);
        return true;
      }
      return false;
    };
    if (evaluate()) return undefined;
    const tick = setInterval(() => {
      if (evaluate()) clearInterval(tick);
    }, STREAMING_RECHECK_MS);
    return () => clearInterval(tick);
  }, [candidate, shown]);

  // The success line lingers, then the source unregisters itself.
  useEffect(() => {
    if (phase !== 'success') return undefined;
    const t = setTimeout(() => setPhase('done'), SUCCESS_LINGER_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Android: consume the captured event and hand the user the native sheet —
  // only ever from this click. Accepted → installed marker + success line;
  // dismissed → 30-day snooze. A missing/rejecting event (already used, or
  // the browser withdrew it) simply leaves nothing to offer; a later
  // beforeinstallprompt re-arms the banner on its own.
  const handleInstall = useCallback(async () => {
    const ev = consumeDeferredInstallPrompt();
    setPromptAvailable(false);
    if (!ev) return;
    try {
      await ev.prompt();
      const choice = await ev.userChoice;
      if (choice && choice.outcome === 'accepted') {
        markInstalled();
        setPhase('success');
      } else {
        snooze(PROMPT_DISMISSED_SNOOZE_MS);
      }
    } catch {
      /* nothing to show — the offer is simply gone until the browser re-offers */
    }
  }, [markInstalled, snooze]);

  const handleOpenGuide = useCallback(() => setSheetOpen(true), []);

  // They have seen the steps: the installed app runs standalone and never
  // sees this banner; if they did not follow through, the offer returns in
  // 14 days rather than nagging on the next page.
  const handleCloseGuide = useCallback(() => {
    setSheetOpen(false);
    snooze(DISMISS_SNOOZE_MS);
  }, [snooze]);

  const handleDismiss = useCallback(() => {
    if (phase === 'success') {
      setPhase('done');
      return;
    }
    snooze(DISMISS_SNOOZE_MS);
  }, [phase, snooze]);

  let descriptor: BannerDescriptor | null = null;
  if (phase === 'success') {
    descriptor = {
      id: 'install-app',
      tone: 'info',
      dismissible: true,
      onDismiss: handleDismiss,
      content: <span data-testid="install-app-success">{INSTALL_COPY.success}</span>,
      shortContent: <span data-testid="install-app-success">{INSTALL_COPY.successShort}</span>,
    };
  } else if (phase === 'offer' && candidate && shown) {
    const isAndroid = platform === 'android';
    descriptor = {
      id: 'install-app',
      tone: 'info',
      dismissible: true,
      onDismiss: handleDismiss,
      content: (
        <>
          <span data-testid="install-app-copy">{isAndroid ? INSTALL_COPY.android : INSTALL_COPY.ios}</span>
          <CtaButton
            type="button"
            onClick={isAndroid ? () => void handleInstall() : handleOpenGuide}
            data-testid="install-app-cta"
          >
            {isAndroid ? INSTALL_COPY.androidCta : INSTALL_COPY.iosCta}
          </CtaButton>
        </>
      ),
      shortContent: (
        <>
          <span data-testid="install-app-copy">
            {isAndroid ? INSTALL_COPY.androidShort : INSTALL_COPY.iosShort}
          </span>
          <CtaButton
            type="button"
            onClick={isAndroid ? () => void handleInstall() : handleOpenGuide}
            data-testid="install-app-cta"
          >
            {isAndroid ? INSTALL_COPY.androidCta : INSTALL_COPY.iosShortCta}
          </CtaButton>
        </>
      ),
    };
  }

  useRegisterBanner(bus ? descriptor : null);

  // Banner-center-native: outside the center it renders nothing. The iOS
  // guidance sheet is the one thing this source paints itself — a body
  // portal, so it lives outside the band regardless.
  if (!bus) return null;
  return <InstallGuideSheet open={sheetOpen} onClose={handleCloseGuide} />;
};

export default InstallAppBanner;
